import React from "react";
import { MembershipStatus } from "../constants/groups/group-membership";

export type AccountStatus = "requested" | "invited" | "accepted" | "rejected";
export type UserStatus = "active" | "inactive";
export enum UserRole {
  ADMIN = "admin",
  MEMBER = "member",
  SUPER_ADMIN = "super-admin",
}

export type GroupMemberRole = "Admin" | "Moderator" | "Member";
export type PendingApprovalStatus = "Pending" | "Rejected";

export type UserPhoto = {
  id: string;
  file_name: string;
  file_size: number | null;
  mime_type: string;
  url: string;
};

export type InvitableMember = Readonly<{
  itemId: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  userName: string;
  email: string;
  profileImageUrl?: string;
}>;

export type GroupMember = {
  itemId: string;
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  role: GroupMemberRole;
  status: MembershipStatus;
  profileImageUrl: string | null;
  joinedAt: string;
};

export type PendingMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  requested_role: GroupMemberRole;
  status: PendingApprovalStatus;
  profile_image_url: string | null;
  requested_at: string;
  message?: string;
};

export type BlocksUserListItem = {
  itemId: string;
  userName: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  email: string;
};

export type UserFilters = {
  page: number;
  perPage: number;
  search?: string;
  role?: UserRole[];
  user_status?: UserStatus;
  start_date?: string;
  end_date?: string;
};

export interface Permission {
  permission_id: string;
  resource: string;
  action: string;
  enabled: boolean;
}

export interface RoleResponse {
  id: string;
  name: string;
  permission_count: number;
  permissions: Permission[];
  user_count: number;
}

export interface FetchUsersParams {
  page?: number;
  perPage?: number;
  search?: string;
  role?: UserRole[];
  user_status?: UserStatus;
  start_date?: string;
  end_date?: string;
}

export interface Pagy {
  total_count: number;
  total_pages: number;
  current_page: number;
  per_page?: number;
  from: number;
  to: number;
  prev_page?: number | null;
  next_page?: number | null;
  first_page?: number;
  last_page?: number;
}

export interface PaginatedUsers {
  users: User[];
  pagy: Pagy;
}

export interface UserStatsResponse {
  all_users: number;
  active_users: number;
  admins: number;
  members: number;
}

// Blocks integration model
export type BlocksUser = Readonly<{
  itemId: string;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  profileImageUrl: string | null;
  createdDate: string;
  /**
   * The user's app roles, independent of any org.
   *
   * `memberships` is empty whenever the session is scoped to the `default`
   * bookkeeping tenant, and the roles used to live only inside it — so they were
   * thrown away in exactly the case where something still needs to label the
   * user. `clouduser` is already stripped (see `appRoles`).
   */
  roles: readonly string[];
  memberships: readonly {
    organizationId: string;
    roles: readonly string[];
    permissions: readonly string[];
  }[];
  phoneNumber: string;
  logInCount: number;
}>;

export type User = Readonly<{
  itemId: string;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  profileImageUrl: string | null;
  createdDate: string;
  memberships: readonly {
    organizationId: string;
    roles: readonly string[];
    permissions: readonly string[];
  }[];
  phoneNumber: string;
  logInCount: number;
  role: UserRole;
}>;

export type UsersResponse = Readonly<{
  users: readonly BlocksUser[];
  totalCount: number;
  pageSize: number;
}>;

export type DateRange = Readonly<{
  start: string;
  end: string;
}>;

// Input / payload types — mutable for form and mutation use
export type InviteUserPayload = {
  email: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
};

// Profile graphql
export type UserProfileItem = {
  ItemId: string;
  CreatedDate: string;
  CreatedBy: string | null;
  LastUpdatedDate: string | null;
  LastUpdatedBy: string | null;
  IsDeleted: boolean;
  Language: string | null;
  OrganizationId: string;
  Tags: readonly string[];
  DeletedDate: string | null;
  gender: string | null;
  designation: string | null;
  department: string | null;
  company: string | null;
  website: string | null;
  address: string | null;
  country: string | null;
  description: string | null;
  backgroundImageUrl: string | null;
  dob: string | null;
  savedPostIds: string[] | null;
  newsSaved: string[];
  newsRead: string[];
  newsDraftIds: string[];
  wikiSaved: string[];
  wikiDraftIds: string[];
  preferredOrganizationId?: string | null;
};

export type GetUserProfilesResponse = {
  getuserprofiles: {
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    items: readonly UserProfileItem[];
  };
};

export type UserProfile = {
  itemId: string;
  appRole: UserRole;
  approved: boolean;
  deactivated: boolean;
  firstTimeLogin: boolean;
};

export type InsertUserProfileInput = {
  gender?: string;
  designation?: string;
  department?: string;
  company?: string;
  website?: string;
  address?: string;
  country?: string;
  description?: string;
  backgroundImageUrl?: string;
  dob?: string | null;
  wikiSaved: string[];
  newsSaved: string[];
  newsRead?: string[];
  newsDraftIds: string[];
  wikiDraftIds: string[];
  preferredOrganizationId?: string | null;
};

export type UpdateUserProfileInput = InsertUserProfileInput;

export type EditProfileRole = "admin" | "member" | "super-admin";

export type EditProfileFormValues = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  gender: string;
  description: string;
  address: string;
  country: string;
  designation: string;
  department: string;
  company: string;
  website: string;
  dob: Date | null;
};

export type UserPreferences = Readonly<{
  lightMode: boolean;
  langage: string;
  notificationsEnabled?: boolean;
}>;

export type UserProfileMutationResponse = Readonly<{
  acknowledged: boolean;
  totalImpactedData: number;
  itemId: string;
  /**
   * Server-side detail. Null on success; carries the reason when the gateway
   * declines the write, which it otherwise reports as `acknowledged: true`.
   */
  message: string | null;
}>;

export type InsertUserProfileResponse = Readonly<{
  insertuserprofile: UserProfileMutationResponse;
}>;

export type UpdateUserProfileResponse = Readonly<{
  updateuserprofile: UserProfileMutationResponse;
}>;

export type DeleteUserProfileResponse = Readonly<{
  deleteuserprofile: UserProfileMutationResponse;
}>;

export type StatItem = Readonly<{
  count: number;
  text: string;
  icon: React.ReactElement;
}>;
export type IconsaxIcon = React.ComponentType<{
  size?: number;
  color?: string;
}>;
